// src/pages/vendors/PurchaseOrdersPage.jsx
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import { usePermissions } from '../../utils/permissions';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  InputAdornment,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Select,
  InputLabel,
  Alert,
  CircularProgress,
  useTheme,
  alpha,
  Fade,
  Stack,
  Tabs,
  Tab,
  LinearProgress,
  Divider,
  Fab
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  SortByAlpha as SortIcon,
  Assignment as AssignmentIcon,
  AccountBox as AccountBoxIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  AccessTime as AccessTimeIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  LocalShipping as LocalShippingIcon,
  Print as PrintIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  Description as DescriptionIcon,
  Receipt as ReceiptIcon,
  CalendarMonth as CalendarMonthIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import POTransactionsTab from './po-transactions-components/POTransactionsTab';
import PODeliveryCalendar from './po-transactions-components/PODeliveryCalendar';

// Styled components
const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
  '&:hover': {
    backgroundColor: theme.palette.action.selected,
  },
  cursor: 'pointer'
}));

const StatusChip = styled(Chip)(({ theme, status }) => {
  let color = 'default';
  switch (status) {
    case 'confirmed':
      color = 'success';
      break;
    case 'received':
      color = 'success';
      break;
    case 'pending':
      color = 'warning';
      break;
    case 'sent':
      color = 'info';
      break;
    case 'draft':
      color = 'default';
      break;
    case 'cancelled':
      color = 'error';
      break;
    default:
      color = 'default';
  }
  
  return {
    fontWeight: 500,
    borderRadius: theme.shape.borderRadius,
    textTransform: 'uppercase',
    fontSize: '0.75rem',
    backgroundColor: color === 'default' ? undefined : theme.palette[color].lighter,
    color: color === 'default' ? undefined : theme.palette[color].main,
    borderColor: color === 'default' ? undefined : theme.palette[color].main,
  };
});


const PurchaseOrdersPage = ({ currentCompany }) => {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const { 
    canViewVendorPurchaseOrders, 
    canEditVendorPurchaseOrders, 
    canDeleteVendorPurchaseOrders 
  } = usePermissions();

  // State - ALL HOOKS MUST BE BEFORE ANY CONDITIONAL RETURNS
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [filteredPOs, setFilteredPOs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [companyName, setCompanyName] = useState('');
  
  // UI State
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('number_desc');
  
  // PO metrics
  const [metrics, setMetrics] = useState({
    total: 0,
    draft: 0,
    sent: 0,
    confirmed: 0,
    cancelled: 0
  });
  
  // Menu state
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedPO, setSelectedPO] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [statusMenuAnchorEl, setStatusMenuAnchorEl] = useState(null);
  const [activeTab,           setActiveTab]           = useState(0);
  const [txStepperOpen,       setTxStepperOpen]       = useState(false);

  // Permission flags
  const canView = canViewVendorPurchaseOrders();
  const canEdit = canEditVendorPurchaseOrders();
  const canDelete = canDeleteVendorPurchaseOrders();
  
  // Load data on mount
  useEffect(() => {
    if (canView) {
      fetchPurchaseOrders();
      fetchCompanyData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, canView]);
  
  // Refresh PO list when switching to Documents tab
  useEffect(() => {
    if (canView && activeTab === 0) {
      fetchPurchaseOrders();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Apply filters when any filter changes
  useEffect(() => {
    if (canView) {
      applyFilters();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purchaseOrders, searchTerm, filterStatus, sortBy, canView]);

  // Fetch purchase orders from Supabase
  const fetchPurchaseOrders = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Fetch purchase orders with suppliers
      const { data, error: fetchError } = await supabase
        .from('vendor_PO')
        .select(`
          *,
          suppliers (id, name, email)
        `)
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });
      
      if (fetchError) throw fetchError;
      
      if (data) {
        setPurchaseOrders(data);
        calculateMetrics(data);
      }
    } catch (err) {
      console.error('Error fetching purchase orders:', err);
      setError('Failed to load purchase orders: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Calculate metrics
  const calculateMetrics = (pos) => {
    const newMetrics = {
      total: pos.length,
      draft: pos.filter(po => po.status === 'draft').length,
      sent: pos.filter(po => po.status === 'sent').length,
      confirmed: pos.filter(po => po.status === 'confirmed').length,
      cancelled: pos.filter(po => po.status === 'cancelled').length
    };
    setMetrics(newMetrics);
  };

  // Apply filters
  const applyFilters = () => {
    let filtered = [...purchaseOrders];
    
    // Apply search term
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(po => 
        po.vendor_PO_number?.toLowerCase().includes(searchLower) ||
        po.suppliers?.name?.toLowerCase().includes(searchLower) ||
        po.notes?.toLowerCase().includes(searchLower)
      );
    }
    
    // Apply status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter(po => po.status === filterStatus);
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'date_desc':
          return new Date(b.vendor_PO_date || b.created_at) - new Date(a.vendor_PO_date || a.created_at);
        case 'date_asc':
          return new Date(a.vendor_PO_date || a.created_at) - new Date(b.vendor_PO_date || b.created_at);
        case 'number_asc':
          {
            if (!a.vendor_PO_number || !b.vendor_PO_number) return 0;
            // Extract numeric parts for proper numeric sorting
            // Handles formats: PO-YYYYMM-NNNN, PO-YYYY-NNNN, PO-YYYYMM-NNN
            const extractNumeric = (str) => {
              // Match: PO-YYYYMM-NNNN or PO-YYYY-NNNN
              const match = str.match(/PO-(\d{4})(\d{2})?-(\d+)/);
              if (match) {
                const year = parseInt(match[1], 10);
                const month = match[2] ? parseInt(match[2], 10) : 0;
                const num = parseInt(match[3], 10);
                // Create a sortable number: YYYYMMNNNN (year*1000000 + month*10000 + num)
                return year * 1000000 + month * 10000 + num;
              }
              // Fallback: try to extract any numbers
              const fallback = str.match(/\d+/g);
              if (fallback && fallback.length > 0) {
                return parseInt(fallback.join(''), 10) || 0;
              }
              return 0;
            };
            const aNum = extractNumeric(a.vendor_PO_number);
            const bNum = extractNumeric(b.vendor_PO_number);
            if (aNum !== bNum) {
              return aNum - bNum;
            }
            // If numeric values are equal, fall back to string comparison
            return a.vendor_PO_number.localeCompare(b.vendor_PO_number);
          }
        case 'number_desc':
          {
            if (!a.vendor_PO_number || !b.vendor_PO_number) return 0;
            // Extract numeric parts for proper numeric sorting
            // Handles formats: PO-YYYYMM-NNNN, PO-YYYY-NNNN, PO-YYYYMM-NNN
            const extractNumeric = (str) => {
              // Match: PO-YYYYMM-NNNN or PO-YYYY-NNNN
              const match = str.match(/PO-(\d{4})(\d{2})?-(\d+)/);
              if (match) {
                const year = parseInt(match[1], 10);
                const month = match[2] ? parseInt(match[2], 10) : 0;
                const num = parseInt(match[3], 10);
                // Create a sortable number: YYYYMMNNNN (year*1000000 + month*10000 + num)
                return year * 1000000 + month * 10000 + num;
              }
              // Fallback: try to extract any numbers
              const fallback = str.match(/\d+/g);
              if (fallback && fallback.length > 0) {
                return parseInt(fallback.join(''), 10) || 0;
              }
              return 0;
            };
            const aNum = extractNumeric(a.vendor_PO_number);
            const bNum = extractNumeric(b.vendor_PO_number);
            if (aNum !== bNum) {
              return bNum - aNum;
            }
            // If numeric values are equal, fall back to string comparison (reversed for desc)
            return b.vendor_PO_number.localeCompare(a.vendor_PO_number);
          }
        case 'expected_delivery_desc':
          return new Date(b.expected_delivery_date || 0) - new Date(a.expected_delivery_date || 0);
        case 'expected_delivery_asc':
          return new Date(a.expected_delivery_date || 0) - new Date(b.expected_delivery_date || 0);
        default:
          return 0;
      }
    });
    
    setFilteredPOs(filtered);
  };

  // Format date without timezone issues
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    // If it's already a formatted date string, return it
    if (typeof dateString === 'string' && dateString.includes('/')) {
      return dateString;
    }
    // Parse date string (YYYY-MM-DD) directly without timezone conversion
    if (typeof dateString === 'string' && dateString.includes('-')) {
      const parts = dateString.split('-');
      if (parts.length === 3) {
        const year = parts[0];
        const month = parts[1];
        const day = parts[2];
        return `${month}/${day}/${year}`;
      }
    }
    // Fallback: use Date but add noon to avoid timezone issues
    const date = new Date(dateString + 'T12:00:00');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  // Fetch company data
  const fetchCompanyData = async () => {
    try {
      if (currentCompany && currentCompany.name) {
        setCompanyName(currentCompany.name);
        return;
      }

      const { data, error } = await supabase
        .from('companies')
        .select('name')
        .eq('id', companyId)
        .single();

      if (error) throw error;

      if (data) {
        setCompanyName(data.name);
      }
    } catch (err) {
      console.error('Error fetching company data:', err);
      setCompanyName('Company Name');
    }
  };

  // Reset all filters
  const resetFilters = () => {
    setSearchTerm('');
    setFilterStatus('all');
    setSortBy('number_desc');
  };

  // Navigate to create new PO
  const handleCreatePO = () => {
    navigate(`/company/${companyId}/purchase-orders/new`);
  };

  // Navigate to edit PO
  const handleEditPO = (po) => {
    navigate(`/company/${companyId}/purchase-orders/${po.id}`);
  };

  // Handle menu open
  const handleMenuOpen = (event, po) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setSelectedPO(po);
  };

  // Handle menu close
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedPO(null);
  };

  // Handle delete
  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
    setMenuAnchorEl(null); // Close menu but keep selectedPO
  };

  const handleDeletePO = async () => {
    if (!selectedPO) return;
    
    try {
      const { error } = await supabase
        .from('vendor_PO')
        .delete()
        .eq('id', selectedPO.id);
      
      if (error) throw error;
      
      await fetchPurchaseOrders();
      setDeleteDialogOpen(false);
      setSelectedPO(null);
    } catch (err) {
      console.error('Error deleting purchase order:', err);
      setError('Failed to delete purchase order: ' + err.message);
      setDeleteDialogOpen(false);
    }
  };

  // Handle status change
  const handleStatusChange = async (newStatus) => {
    if (!selectedPO) return;

    try {
      const { error } = await supabase
        .from('vendor_PO')
        .update({ status: newStatus })
        .eq('id', selectedPO.id);

      if (error) throw error;

      await fetchPurchaseOrders();
      setStatusMenuAnchorEl(null);
      handleMenuClose();
      setSelectedPO(null);
    } catch (err) {
      console.error('Error updating status:', err);
      setError('Failed to update status: ' + err.message);
      setStatusMenuAnchorEl(null);
    }
  };

  const handleStatusMenuOpen = (event) => {
    setStatusMenuAnchorEl(event.currentTarget);
  };

  const handleStatusMenuClose = () => {
    setStatusMenuAnchorEl(null);
  };

  // Handle view PO
  const handleViewPO = (po) => {
    navigate(`/company/${companyId}/purchase-orders/${po.id}`);
  };

  // Permission check - AFTER all hooks
  if (!canView) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          minHeight: '70vh',
          textAlign: 'center'
        }}>
          <Card sx={{
            p: 8,
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            background: 'white',
            boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
            maxWidth: 600,
            width: '100%',
            position: 'relative',
            overflow: 'hidden',
            cursor: 'default',
            transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: `0 20px 60px ${alpha(theme.palette.grey[900], 0.15)}`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.grey[400]})`,
              opacity: 0.8,
              transition: 'opacity 0.3s ease'
            },
            '&:hover::before': {
              opacity: 1
            }
          }}>
            <Box sx={{ 
              p: 3, 
              borderRadius: 3, 
              background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              mb: 6,
              display: 'inline-flex',
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
            }}>
              <AssignmentIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
            </Box>
            <Typography variant="h3" sx={{ 
              mb: 3, 
              fontWeight: 500, 
              color: 'text.primary',
              fontSize: '2.5rem',
              letterSpacing: '-0.8px'
            }}>
              Access Restricted
            </Typography>
            <Typography variant="h6" sx={{ 
              color: 'text.secondary', 
              mb: 4,
              fontWeight: 500,
              fontSize: '1.2rem'
            }}>
              Vendor Purchase Orders Permissions Required
            </Typography>
            <Typography variant="body1" sx={{ 
              color: 'text.secondary',
              lineHeight: 1.7,
              mb: 6,
              fontSize: '1.05rem',
              maxWidth: 450,
              mx: 'auto'
            }}>
              You don't currently have the necessary permissions to access Vendor Purchase Orders. 
              Contact your system administrator to request access or verify your role permissions.
            </Typography>
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                px: 4,
                py: 1.5,
                fontSize: '1rem'
              }}
            >
              Return to Previous Page
            </Button>
          </Card>
        </Box>
      </Container>
    );
  }

  return (
    <>
    <Fade in timeout={800}>
      <Box>
        <Container 
          maxWidth="xl"
          sx={{
            py: 3,
            pb: 8,
            px: { xs: 2, sm: 3 },
            width: '100%',
            maxWidth: '100vw !important'
          }}
        >
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Header Section */}
      <Box sx={{ mb: 5, pt: 3 }}>
        {/* Enhanced Page Header */}
        <Card sx={{
          borderRadius: 3,
          mb: 3,
          mt: 5,
          border: `1px solid ${theme.palette.grey[400]}`,
          background: theme.palette.grey[100],
          backdropFilter: 'blur(20px)',
          boxShadow: 'none',
          borderTop: '3px solid #9C27B0',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: `0 6px 20px ${alpha('#9C27B0', 0.15)}`,
          },
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 2 }}>
              <Box sx={{
                p: 2,
                borderRadius: 3,
                background: alpha('#9C27B0', 0.08),
                border: `1px solid ${alpha('#9C27B0', 0.15)}`,
              }}>
                <AssignmentIcon sx={{ fontSize: 32, color: '#9C27B0' }} />
              </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h4" sx={{
                fontWeight: 500,
                color: '#9C27B0',
                mb: 1,
                fontSize: '2rem',
                letterSpacing: '-0.5px'
              }}>
                Purchase Orders
              </Typography>
              <Typography variant="subtitle1" sx={{ 
                color: 'text.secondary', 
                fontWeight: 500,
                fontSize: '1rem'
              }}>
                {companyName || 'Loading...'}
              </Typography>
            </Box>
            {canEdit && activeTab === 0 && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreatePO}
                sx={{
                  borderRadius: 2,
                  fontWeight: 500,
                  px: 3,
                  py: 1.5,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                  boxShadow: `0 6px 18px ${alpha(theme.palette.primary.main, 0.25)}`,
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.35)}`
                  }
                }}
              >
                New Purchase Order
              </Button>
            )}
            {activeTab === 1 && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setTxStepperOpen(true)}
                sx={{
                  borderRadius: 2,
                  fontWeight: 500,
                  px: 3,
                  py: 1.5,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                  boxShadow: `0 6px 18px ${alpha(theme.palette.primary.main, 0.25)}`,
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.35)}`
                  }
                }}
              >
                Create PO Transaction
              </Button>
            )}
          </Box>
          <Typography variant="body2" sx={{ 
            color: 'text.secondary', 
            maxWidth: 700, 
            lineHeight: 1.6,
            fontSize: '0.95rem',
            fontWeight: 500
          }}>
            Manage purchase orders for inventory items from your suppliers.
          </Typography>
        </CardContent>
      </Card>
    </Box>

      {/* Summary Cards */}
      <Box sx={{ 
        mb: 5, 
        px: 1,
        animation: 'slideInUp 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '@keyframes slideInUp': {
          '0%': {
            transform: 'translateY(100%)',
            opacity: 0,
          },
          '100%': {
            transform: 'translateY(0)',
            opacity: 1,
          }
        }
      }}>
        <Grid container spacing={6} sx={{ maxWidth: '100%', width: '100%' }}>
          <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 0 }}>
            <Card 
              sx={{ 
                height: '100%',
                background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.08)}, ${alpha(theme.palette.info.main, 0.02)})`,
                border: `1px solid ${theme.palette.info.main}`,
                borderRadius: 3,
                boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
                transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                position: 'relative',
                overflow: 'hidden',
                cursor: 'default',
                '&:hover': { 
                  transform: 'translateY(-4px)',
                  boxShadow: `0 20px 40px ${alpha(theme.palette.info.main, 0.25)}`,
                  bgcolor: 'white'
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.03)}, transparent)`,
                  zIndex: 0,
                  transition: 'opacity 0.3s ease',
                },
                '&:hover::before': {
                  opacity: 0.8
                }
              }}
            >
              <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography variant="h3" sx={{ 
                      fontWeight: 500, 
                      color: 'info.main', 
                      mb: 1,
                      fontSize: '2rem',
                      lineHeight: 1
                    }}>
                      {metrics.total}
                    </Typography>
                    <Typography variant="body1" color="text.primary" sx={{ 
                      fontWeight: 500,
                      fontSize: '1rem'
                    }}>
                      Total Purchase Orders
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mt: 0.5 }}>
                      {metrics.draft} draft{metrics.draft !== 1 && 's'}
                    </Typography>
                  </Box>
                  <Box 
                    sx={{ 
                      p: 2.5, 
                      borderRadius: 3, 
                      background: theme.palette.info.main,
                      color: 'white',
                      boxShadow: `0 6px 18px ${alpha(theme.palette.info.main, 0.3)}`,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`
                    }}
                  >
                    <AssignmentIcon sx={{ fontSize: 32 }} />
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 0 }}>
            <Card 
              sx={{ 
                height: '100%',
                background: `linear-gradient(135deg, ${alpha(theme.palette.grey[600], 0.08)}, ${alpha(theme.palette.grey[600], 0.02)})`,
                border: `1px solid ${theme.palette.grey[600]}`,
                borderRadius: 3,
                boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
                transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                position: 'relative',
                overflow: 'hidden',
                cursor: 'default',
                '&:hover': { 
                  transform: 'translateY(-4px)',
                  boxShadow: `0 20px 40px ${alpha(theme.palette.grey[600], 0.25)}`,
                  bgcolor: 'white'
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.grey[600], 0.03)}, transparent)`,
                  zIndex: 0,
                  transition: 'opacity 0.3s ease',
                },
                '&:hover::before': {
                  opacity: 0.8
                }
              }}
            >
              <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography variant="h3" sx={{ 
                      fontWeight: 500, 
                      color: 'grey.600', 
                      mb: 1,
                      fontSize: '2rem',
                      lineHeight: 1
                    }}>
                      {metrics.draft}
                    </Typography>
                    <Typography variant="body1" color="text.primary" sx={{ 
                      fontWeight: 500,
                      fontSize: '1rem'
                    }}>
                      Draft
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mt: 0.5 }}>
                      Not yet sent
                    </Typography>
                  </Box>
                  <Box 
                    sx={{ 
                      p: 2.5, 
                      borderRadius: 3, 
                      background: theme.palette.grey[600],
                      color: 'white',
                      boxShadow: `0 6px 18px ${alpha(theme.palette.grey[600], 0.3)}`,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`
                    }}
                  >
                    <InfoIcon sx={{ fontSize: 32 }} />
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 0 }}>
            <Card 
              sx={{ 
                height: '100%',
                background: `linear-gradient(135deg, ${alpha('#FF9800', 0.08)}, ${alpha('#FF9800', 0.02)})`,
                border: `1px solid #FF9800`,
                borderRadius: 3,
                boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
                transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                position: 'relative',
                overflow: 'hidden',
                cursor: 'default',
                '&:hover': { 
                  transform: 'translateY(-4px)',
                  boxShadow: `0 20px 40px ${alpha('#FF9800', 0.25)}`,
                  bgcolor: 'white'
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: `linear-gradient(135deg, ${alpha('#FF9800', 0.03)}, transparent)`,
                  zIndex: 0,
                  transition: 'opacity 0.3s ease',
                },
                '&:hover::before': {
                  opacity: 0.8
                }
              }}
            >
              <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography variant="h3" sx={{ 
                      fontWeight: 500, 
                      color: '#FF9800', 
                      mb: 1,
                      fontSize: '2rem',
                      lineHeight: 1
                    }}>
                      {metrics.sent}
                    </Typography>
                    <Typography variant="body1" color="text.primary" sx={{ 
                      fontWeight: 500,
                      fontSize: '1rem'
                    }}>
                      Sent
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mt: 0.5 }}>
                      Awaiting confirmation
                    </Typography>
                  </Box>
                  <Box 
                    sx={{ 
                      p: 2.5, 
                      borderRadius: 3, 
                      background: '#FF9800',
                      color: 'white',
                      boxShadow: `0 6px 18px ${alpha('#FF9800', 0.3)}`,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`
                    }}
                  >
                    <LocalShippingIcon sx={{ fontSize: 32 }} />
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 0 }}>
            <Card 
              sx={{ 
                height: '100%',
                background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.08)}, ${alpha(theme.palette.success.main, 0.02)})`,
                border: `1px solid ${theme.palette.success.main}`,
                borderRadius: 3,
                boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
                transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                position: 'relative',
                overflow: 'hidden',
                cursor: 'default',
                '&:hover': { 
                  transform: 'translateY(-4px)',
                  boxShadow: `0 20px 40px ${alpha(theme.palette.success.main, 0.25)}`,
                  bgcolor: 'white'
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.03)}, transparent)`,
                  zIndex: 0,
                  transition: 'opacity 0.3s ease',
                },
                '&:hover::before': {
                  opacity: 0.8
                }
              }}
            >
              <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography variant="h3" sx={{ 
                      fontWeight: 500, 
                      color: 'success.main', 
                      mb: 1,
                      fontSize: '2rem',
                      lineHeight: 1
                    }}>
                      {metrics.confirmed}
                    </Typography>
                    <Typography variant="body1" color="text.primary" sx={{ 
                      fontWeight: 500,
                      fontSize: '1rem'
                    }}>
                      Confirmed
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mt: 0.5 }}>
                      {((metrics.confirmed / (metrics.total || 1)) * 100).toFixed(0)}% confirmed
                    </Typography>
                  </Box>
                  <Box 
                    sx={{ 
                      p: 2.5, 
                      borderRadius: 3, 
                      background: theme.palette.success.main,
                      color: 'white',
                      boxShadow: `0 6px 18px ${alpha(theme.palette.success.main, 0.3)}`,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`
                    }}
                  >
                    <CheckCircleIcon sx={{ fontSize: 32 }} />
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Tabs */}
      <Box sx={{ mb: 3 }}>
        <Paper sx={{
          borderRadius: 3,
          overflow: 'hidden',
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          boxShadow: `0 12px 32px ${alpha(theme.palette.grey[900], 0.12)}`,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
          }
        }}>
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            sx={{
              px: 2, pt: 1,
              '& .MuiTab-root': {
                fontWeight: 500,
                fontSize: '0.9rem',
                textTransform: 'none',
                minHeight: 60,
                px: 3,
                mx: 1,
                borderRadius: '8px 8px 0 0',
                transition: 'all 0.2s ease',
              },
              '& .MuiTabs-indicator': {
                height: 3,
                borderRadius: '3px 3px 0 0'
              }
            }}
          >
            <Tab label="Purchase Order Documents"   icon={<DescriptionIcon />}    iconPosition="start" sx={{ minWidth: 200 }} />
            <Tab label="Purchase Order Transactions" icon={<ReceiptIcon />}        iconPosition="start" sx={{ minWidth: 220 }} />
            <Tab label="Delivery Calendar"           icon={<CalendarMonthIcon />}  iconPosition="start" sx={{ minWidth: 180 }} />
          </Tabs>
        </Paper>
      </Box>

      {activeTab === 1 && <POTransactionsTab companyId={companyId} stepperOpen={txStepperOpen} onStepperOpenChange={setTxStepperOpen} />}
      {activeTab === 2 && <PODeliveryCalendar companyId={companyId} />}

      {activeTab === 0 && <>
      {/* Filters */}
      <Paper sx={{
        p: 3,
        mb: 3,
        borderRadius: 3,
        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
        background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
        boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`
      }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Search purchase orders..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: theme.palette.primary.main }} />
                  </InputAdornment>
                ),
                sx: { 
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  background: 'white',
                  '&:hover': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                  },
                  '&.Mui-focused': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.25)}`
                  }
                }
              }}
              size="medium"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    border: 'none'
                  },
                  '&:hover fieldset': {
                    border: 'none'
                  },
                  '&.Mui-focused fieldset': {
                    border: 'none'
                  }
                }
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="medium">
              <InputLabel 
                id="status-filter-label" 
                sx={{ 
                  fontSize: '0.9rem', 
                  fontWeight: 500,
                  transform: 'translate(14px, 12px) scale(1)',
                  '&.Mui-focused, &.MuiFormLabel-filled': {
                    fontSize: '0.9rem',
                    transform: 'translate(14px, -20px) scale(0.75)'
                  }
                }}
              >
                Status
              </InputLabel>
              <Select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                label="Status"
                sx={{
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  background: 'white',
                  minHeight: 52,
                  '& .MuiSelect-select': {
                    py: 1.5,
                    px: 2,
                    fontSize: '0.9rem',
                    fontWeight: 500,
                    minHeight: 'auto',
                    display: 'flex',
                    alignItems: 'center'
                  },
                  '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                  '&:hover': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                  },
                  '&.Mui-focused': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                  }
                }}
              >
                <MenuItem value="all" sx={{ fontSize: '0.9rem', fontWeight: 500, py: 1.5, px: 2 }}>All Statuses</MenuItem>
                <MenuItem value="draft" sx={{ fontSize: '0.9rem', fontWeight: 500, py: 1.5, px: 2 }}>Draft</MenuItem>
                <MenuItem value="sent" sx={{ fontSize: '0.9rem', fontWeight: 500, py: 1.5, px: 2 }}>Sent</MenuItem>
                <MenuItem value="confirmed" sx={{ fontSize: '0.9rem', fontWeight: 500, py: 1.5, px: 2 }}>Confirmed</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="medium">
              <InputLabel 
                id="sort-by-label" 
                sx={{ 
                  fontSize: '0.9rem', 
                  fontWeight: 500,
                  transform: 'translate(14px, 12px) scale(1)',
                  '&.Mui-focused, &.MuiFormLabel-filled': {
                    fontSize: '0.9rem',
                    transform: 'translate(14px, -20px) scale(0.75)'
                  }
                }}
              >
                Sort By
              </InputLabel>
              <Select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                label="Sort By"
                sx={{
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  background: 'white',
                  minHeight: 52,
                  '& .MuiSelect-select': {
                    py: 1.5,
                    px: 2,
                    fontSize: '0.9rem',
                    fontWeight: 500,
                    minHeight: 'auto',
                    display: 'flex',
                    alignItems: 'center'
                  },
                  '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                  '&:hover': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                  },
                  '&.Mui-focused': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                  }
                }}
              >
                <MenuItem value="date_desc" sx={{ fontSize: '0.9rem', fontWeight: 500, py: 1.5, px: 2 }}>Date (Newest First)</MenuItem>
                <MenuItem value="date_asc" sx={{ fontSize: '0.9rem', fontWeight: 500, py: 1.5, px: 2 }}>Date (Oldest First)</MenuItem>
                <MenuItem value="number_asc" sx={{ fontSize: '0.9rem', fontWeight: 500, py: 1.5, px: 2 }}>PO # (A-Z)</MenuItem>
                <MenuItem value="number_desc" sx={{ fontSize: '0.9rem', fontWeight: 500, py: 1.5, px: 2 }}>PO # (Z-A)</MenuItem>
                <MenuItem value="expected_delivery_desc" sx={{ fontSize: '0.9rem', fontWeight: 500, py: 1.5, px: 2 }}>Expected Delivery (Latest)</MenuItem>
                <MenuItem value="expected_delivery_asc" sx={{ fontSize: '0.9rem', fontWeight: 500, py: 1.5, px: 2 }}>Expected Delivery (Earliest)</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                {filteredPOs.length} purchase order{filteredPOs.length !== 1 && 's'} found
              </Typography>
              
              <Button 
                size="small" 
                onClick={resetFilters}
                disabled={searchTerm === '' && filterStatus === 'all' && sortBy === 'number_desc'}
                sx={{ 
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  fontWeight: 500,
                  '&:hover': {
                    transform: 'translateY(-1px)',
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
                  }
                }}
              >
                Reset Filters
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress size={60} sx={{ color: '#9C27B0' }} />
        </Box>
      ) : filteredPOs.length === 0 ? (
        <Paper 
          sx={{
            textAlign: 'center',
            py: 8,
            px: 4,
            border: '2px dashed',
            borderColor: 'divider',
            borderRadius: 2,
          }}
        >
          <Box sx={{ 
            p: 2.5, 
            borderRadius: 3, 
            background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            backdropFilter: 'blur(10px)',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.3)}`,
            mb: 3,
            width: 'fit-content',
            mx: 'auto'
          }}>
            <AssignmentIcon sx={{ fontSize: 36, color: 'text.secondary' }} />
          </Box>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
            {purchaseOrders.length === 0 ? 'No Purchase Orders Yet' : 'No Matching Purchase Orders'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto', fontWeight: 500 }}>
            {purchaseOrders.length === 0 
              ? 'Create your first purchase order to request inventory items from suppliers.'
              : 'Try adjusting your search or filter criteria.'}
          </Typography>
          {purchaseOrders.length === 0 && canEdit && (
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleCreatePO}
              sx={{ 
                borderRadius: 2,
                borderColor: 'grey.400',
                color: 'primary.main',
                borderWidth: 1,
                fontWeight: 500,
                px: 3,
                py: 1.5,
                background: 'white',
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                '&:hover': {
                  borderColor: 'grey.600',
                  backgroundColor: 'white',
                  transform: 'translateY(-1px)',
                  boxShadow: `0 4px 12px ${alpha(theme.palette.grey[400], 0.25)}`
                }
              }}
            >
              Create Your First Purchase Order
            </Button>
          )}
        </Paper>
      ) : (
        <Paper sx={{ 
          borderRadius: 3, 
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
          boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
          overflow: 'hidden',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          animation: 'slideInUp 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '@keyframes slideInUp': {
            '0%': {
              transform: 'translateY(100%)',
              opacity: 0,
            },
            '100%': {
              transform: 'translateY(0)',
              opacity: 1,
            }
          },
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
          }
        }}>
          <TableContainer sx={{ overflow: 'visible' }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell 
                    sx={{ 
                      fontWeight: 500,
                      fontSize: '0.9rem',
                      bgcolor: 'white',
                      borderBottom: `2px solid ${theme.palette.primary.main}`,
                      color: 'primary.main',
                      py: 2,
                      px: 3
                    }}
                  >
                    PO Number
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      fontWeight: 500,
                      fontSize: '0.9rem',
                      bgcolor: 'white',
                      borderBottom: `2px solid ${theme.palette.primary.main}`,
                      color: 'primary.main',
                      py: 2,
                      px: 3
                    }}
                  >
                    Supplier
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      fontWeight: 500,
                      fontSize: '0.9rem',
                      bgcolor: 'white',
                      borderBottom: `2px solid ${theme.palette.primary.main}`,
                      color: 'primary.main',
                      py: 2,
                      px: 3
                    }}
                  >
                    PO Date
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      fontWeight: 500,
                      fontSize: '0.9rem',
                      bgcolor: 'white',
                      borderBottom: `2px solid ${theme.palette.primary.main}`,
                      color: 'primary.main',
                      py: 2,
                      px: 3
                    }}
                  >
                    Expected Delivery
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      fontWeight: 500,
                      fontSize: '0.9rem',
                      bgcolor: 'white',
                      borderBottom: `2px solid ${theme.palette.primary.main}`,
                      color: 'primary.main',
                      py: 2,
                      px: 3
                    }}
                  >
                    Status
                  </TableCell>
                  <TableCell 
                    align="right"
                    sx={{ 
                      fontWeight: 500,
                      fontSize: '0.9rem',
                      bgcolor: 'white',
                      borderBottom: `2px solid ${theme.palette.primary.main}`,
                      color: 'primary.main',
                      py: 2,
                      px: 3,
                      width: 80
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredPOs
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((po) => (
                    <StyledTableRow 
                      key={po.id}
                      onClick={() => handleViewPO(po)}
                    >
                      <TableCell sx={{ py: 2, px: 3 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {po.vendor_PO_number}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 2, px: 3 }}>
                        <Typography variant="body2">
                          {po.suppliers?.name || 'No Supplier'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 2, px: 3 }}>
                        <Typography variant="body2">
                          {formatDate(po.vendor_PO_date)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 2, px: 3 }}>
                        <Typography variant="body2">
                          {formatDate(po.expected_delivery_date)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 2, px: 3 }}>
                        <StatusChip 
                          label={po.status || 'draft'} 
                          status={po.status || 'draft'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right" sx={{ py: 2, px: 3 }} onClick={(e) => e.stopPropagation()}>
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, po)}
                          sx={{
                            borderRadius: 2,
                            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                            '&:hover': {
                              transform: 'scale(1.1)',
                              border: `1px solid ${theme.palette.primary.main}`,
                              boxShadow: `0 2px 8px ${alpha(theme.palette.grey[900], 0.2)}`
                            }
                          }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </StyledTableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          <Box sx={{ 
            borderTop: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
            borderRadius: '0 0 12px 12px'
          }}>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={filteredPOs.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              sx={{ 
                px: 3,
                py: 2,
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                  fontSize: '0.9rem',
                  fontWeight: 500,
                  color: theme.palette.text.primary
                },
                '& .MuiTablePagination-select': {
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  borderRadius: 2,
                  px: 2,
                  py: 1,
                  fontWeight: 500,
                  fontSize: '0.9rem',
                  background: 'white',
                  '&:hover': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                  }
                },
                '& .MuiTablePagination-actions': {
                  '& .MuiIconButton-root': {
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    borderRadius: 2,
                    margin: '0 2px',
                    background: 'white',
                    color: theme.palette.primary.main,
                    fontWeight: 500,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      background: theme.palette.primary.main,
                      color: 'white',
                      transform: 'translateY(-1px)',
                      border: `1px solid ${theme.palette.primary.main}`,
                      boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                    },
                    '&.Mui-disabled': {
                      background: alpha(theme.palette.grey[300], 0.5),
                      color: theme.palette.grey[500],
                      border: `1px solid ${theme.palette.grey[300]}`,
                      transform: 'none',
                      boxShadow: 'none'
                    }
                  }
                }
              }}
            />
          </Box>
        </Paper>
      )}

      </>}

      {/* Action Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            boxShadow: `0 8px 24px ${alpha(theme.palette.grey[900], 0.15)}`,
            minWidth: 200
          }
        }}
      >
        <MenuItem 
          onClick={() => {
            if (selectedPO) handleViewPO(selectedPO);
            handleMenuClose();
          }}
          sx={{ 
            borderRadius: 1,
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.08)
            }
          }}
        >
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="View" />
        </MenuItem>
        <Divider sx={{ my: 1 }} />
        {canEdit && (
          <MenuItem 
            onClick={() => {
              if (selectedPO) handleEditPO(selectedPO);
              handleMenuClose();
            }}
            sx={{ 
              borderRadius: 1,
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.08)
              }
            }}
          >
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Edit" />
          </MenuItem>
        )}
        {canEdit && (
          <MenuItem 
            onClick={handleStatusMenuOpen}
            sx={{ 
              borderRadius: 1,
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.08)
              }
            }}
          >
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'primary.main' }} />
            </ListItemIcon>
            <ListItemText primary="Status" />
          </MenuItem>
        )}
        {canDelete && (
          <MenuItem 
            onClick={handleDeleteClick}
            sx={{ 
              borderRadius: 1,
              color: 'error.main',
              '&:hover': {
                backgroundColor: alpha(theme.palette.error.main, 0.08)
              }
            }}
          >
            <ListItemIcon>
              <DeleteIcon fontSize="small" sx={{ color: 'error.main' }} />
            </ListItemIcon>
            <ListItemText primary="Delete" />
          </MenuItem>
        )}
      </Menu>

      {/* Status Change Menu */}
      <Menu
        anchorEl={statusMenuAnchorEl}
        open={Boolean(statusMenuAnchorEl)}
        onClose={handleStatusMenuClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            boxShadow: `0 8px 24px ${alpha(theme.palette.grey[900], 0.15)}`,
            mt: 1
          }
        }}
      >
        <MenuItem 
          onClick={() => handleStatusChange('draft')}
          disabled={selectedPO?.status === 'draft'}
          sx={{
            fontSize: '0.9rem',
            fontWeight: 500,
            py: 1.5,
            px: 2,
            '&:hover': {
              backgroundColor: alpha(theme.palette.grey[400], 0.1)
            }
          }}
        >
          Draft
        </MenuItem>
        <MenuItem 
          onClick={() => handleStatusChange('sent')}
          disabled={selectedPO?.status === 'sent'}
          sx={{
            fontSize: '0.9rem',
            fontWeight: 500,
            py: 1.5,
            px: 2,
            '&:hover': {
              backgroundColor: alpha(theme.palette.warning.main, 0.1)
            }
          }}
        >
          Sent
        </MenuItem>
        <MenuItem 
          onClick={() => handleStatusChange('confirmed')}
          disabled={selectedPO?.status === 'confirmed'}
          sx={{
            fontSize: '0.9rem',
            fontWeight: 500,
            py: 1.5,
            px: 2,
            '&:hover': {
              backgroundColor: alpha(theme.palette.success.main, 0.1)
            }
          }}
        >
          Confirmed
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            boxShadow: `0 8px 24px ${alpha(theme.palette.grey[900], 0.15)}`
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 500, fontSize: '1.2rem' }}>
          Delete Purchase Order
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete purchase order <strong>{selectedPO?.vendor_PO_number}</strong>? 
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={() => setDeleteDialogOpen(false)}
            sx={{ 
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              fontWeight: 500,
              px: 3,
              py: 1,
              '&:hover': {
                border: `1px solid ${theme.palette.primary.main}`,
                transform: 'translateY(-1px)',
                boxShadow: `0 4px 12px ${alpha(theme.palette.grey[900], 0.15)}`
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeletePO} 
            color="error"
            variant="contained"
            sx={{ 
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              fontWeight: 500,
              px: 3,
              py: 1,
              '&:hover': {
                border: `1px solid ${theme.palette.error.main}`,
                transform: 'translateY(-1px)',
                boxShadow: `0 4px 12px ${alpha(theme.palette.error.main, 0.25)}`
              }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
        </Container>
      </Box>
    </Fade>

    {/* FAB — Tab-aware action button (outside Fade to avoid single-child constraint) */}
    {(activeTab === 0 || activeTab === 1) && createPortal(
      <Fab
        onClick={activeTab === 0 ? handleCreatePO : () => setTxStepperOpen(true)}
        sx={{
          position: 'fixed',
          bottom: 32,
          right: 32,
          width: 80,
          height: 80,
          zIndex: 9999,
          background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
          color: 'white',
          boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.4)}`,
          '&:hover': {
            transform: 'scale(1.08)',
            boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.55)}`,
          },
          transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <AddIcon sx={{ fontSize: 36 }} />
      </Fab>,
      document.body
    )}
  </>
  );
};

export default PurchaseOrdersPage;